import { useEffect } from "react";

/**
 * usePageMeta , minimal SEO helper for an SPA.
 *
 * Sets:
 *  - document.title
 *  - <meta name="description">
 *  - <link rel="canonical">
 *  - <meta property="og:title">, og:description, og:url
 *  - <meta name="twitter:title">, twitter:description
 *
 * Plus optional JSON-LD structured data injected as a tagged <script> tag
 * (re-keyed on path change so it never duplicates).
 *
 * Notes:
 *  - Google's crawler runs JavaScript, so client-side meta updates DO count
 *    for ranking. Social-card scrapers (Facebook, X, LinkedIn) read static
 *    HTML and will fall back to the defaults in `index.html`.
 *  - We never delete the default tags ; we overwrite their values, so if the
 *    user navigates away the most-recent value persists harmlessly.
 */
export function usePageMeta({
  title,
  description,
  path = "",
  jsonLd = null,
}) {
  useEffect(() => {
    if (title) {
      document.title = title;
    }

    // Preview / staging environments must NEVER show up in search results ,
    // otherwise duplicate-content penalties split authority away from
    // www.meditalk.world. Decision is made at runtime from the live
    // hostname (NOT from a build-time env var) so production never
    // accidentally inherits a `noindex` setting if the same .env file
    // gets reused across environments.
    const hostname =
      typeof window !== "undefined" && window.location
        ? window.location.hostname
        : "";
    const isProductionHost =
      hostname === "meditalk.world" || hostname === "www.meditalk.world";
    const isNoIndex = !!hostname && !isProductionHost;

    const setMeta = (selector, attr, value) => {
      if (!value) return;
      const el = document.head.querySelector(selector);
      if (el) {
        el.setAttribute(attr, value);
      } else {
        // Re-create if it was missing (shouldn't be, but safe).
        const fresh = document.createElement("meta");
        const [attrName, attrValue] = selector
          .replace(/^meta\[/, "")
          .replace(/\]$/, "")
          .split("=");
        fresh.setAttribute(attrName, (attrValue || "").replace(/"/g, ""));
        fresh.setAttribute(attr, value);
        document.head.appendChild(fresh);
      }
    };

    if (isNoIndex) {
      // Hard-block indexing on any non-production environment.
      setMeta('meta[name="robots"]', "content", "noindex, nofollow");
      setMeta('meta[name="googlebot"]', "content", "noindex, nofollow");
    }

    if (description) {
      setMeta('meta[name="description"]', "content", description);
      setMeta('meta[property="og:description"]', "content", description);
      setMeta('meta[name="twitter:description"]', "content", description);
    }
    if (title) {
      setMeta('meta[property="og:title"]', "content", title);
      setMeta('meta[name="twitter:title"]', "content", title);
    }

    // Canonical URL , always relative to production domain so search engines
    // index meditalk.world even when crawled from the preview subdomain.
    const canonicalHref = `https://www.meditalk.world${path || ""}`;
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", canonicalHref);
    setMeta('meta[property="og:url"]', "content", canonicalHref);

    // JSON-LD structured data , append a route-scoped script tag.
    const scriptId = "page-jsonld";
    const existing = document.getElementById(scriptId);
    if (existing) existing.remove();
    if (jsonLd) {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.id = scriptId;
      s.text = JSON.stringify(jsonLd);
      document.head.appendChild(s);
    }
  }, [title, description, path, jsonLd]);
}

export default usePageMeta;
